import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * UpgradePrompt Modal Component
 *
 * Shows when user tries to access a feature or model they don't have permission for
 *
 * Props:
 * - show: boolean - whether to show the modal
 * - onClose: function - callback when user closes modal
 * - type: 'feature' | 'model' - what is being restricted
 * - featureName: string - name of the restricted feature (for display)
 * - currentPlan: string - user's current plan name
 * - requiredPlan: string - minimum plan required
 * - message: string - custom message (optional)
 */
const UpgradePrompt = ({
  show,
  onClose,
  type = 'feature',
  featureName = '',
  currentPlan = 'Free',
  requiredPlan = 'Basic',
  message = ''
}) => {
  const navigate = useNavigate();

  if (!show) return null;

  const handleUpgrade = () => {
    onClose();
    navigate('/subscription');
  };

  // Determine icon based on type
  const getIcon = () => {
    if (type === 'model') {
      return (
        <svg className="w-16 h-16 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    }
    return (
      <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    );
  };

  // Get plan color
  const getPlanColor = (plan) => {
    const planLower = plan.toLowerCase();
    if (planLower.includes('professional') || planLower.includes('pro')) {
      return 'text-purple-600 bg-purple-100';
    }
    if (planLower.includes('basic')) {
      return 'text-blue-600 bg-blue-100';
    }
    return 'text-gray-600 bg-gray-100';
  };

  // Default messages
  const getDefaultMessage = () => {
    if (type === 'model') {
      return `This AI model requires a ${requiredPlan} plan or higher.`;
    }
    return `This feature requires a ${requiredPlan} plan or higher.`;
  };

  const displayMessage = message || getDefaultMessage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-fadeIn">
        {/* Icon */}
        <div className="text-center mb-6">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full flex items-center justify-center mb-4">
            {getIcon()}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Upgrade Required
          </h3>
        </div>

        {/* Feature/Model Name */}
        {featureName && (
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-600 mb-1">Trying to access:</p>
            <p className="text-lg font-semibold text-gray-900">{featureName}</p>
          </div>
        )}

        {/* Message */}
        <div className="mb-6">
          <p className="text-gray-700 text-center leading-relaxed">
            {displayMessage}
          </p>
        </div>

        {/* Plan Comparison */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-xs text-gray-500 mb-1">Current Plan</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getPlanColor(currentPlan)}`}>
                {currentPlan}
              </span>
            </div>

            <div className="px-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>

            <div className="text-center flex-1">
              <p className="text-xs text-gray-500 mb-1">Required Plan</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getPlanColor(requiredPlan)}`}>
                {requiredPlan}
              </span>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-900 mb-3">Unlock with {requiredPlan}:</p>
          <ul className="space-y-2">
            {type === 'feature' && (
              <>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-700">Access to {featureName}</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-700">Higher generation limits</span>
                </li>
              </>
            )}
            {type === 'model' && (
              <>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-700">Access to advanced AI models</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-700">Better quality results</span>
                </li>
              </>
            )}
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">Priority support</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
